import React, { CSSProperties, FC, MutableRefObject, ReactNode, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { TableLayoutWrap } from './style';
import { Splitter, Table, TableProps } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import { flushSync } from 'react-dom';
import { debounce } from 'lodash';
import ResizableTable from '../../components/ResizableTable';

export interface TableLayoutRef {
	tableHeight: number;
	resetTablePage: () => void;
	offset: number;
	pageSize: number;
	changeTablePageSize: (pageSize: number) => void;
}

export interface ITableLayout extends TableProps {
	headerChildren: React.ReactNode;
	headerSub?: ReactNode;
	tableLayoutRef?: MutableRefObject<TableLayoutRef | null>;
	total: number;
	handleChange?: (offset: number) => void;
	scrollX?: number | 'max-content';
	rightContent?: React.ReactNode;
	inputWrapStyle?: CSSProperties;
	enableColumnResize?: boolean;
	extHeight?: number;		//给一个补偿高度，针对不同的使用场景赋值抵消留白或者页码消失问题
}

export const DEFAULT_PAGE_SIZE = 30;
export const DEFAULT_PAGE_SIZES = [DEFAULT_PAGE_SIZE, 50, 100, 200, 500];

const STableLayout: FC<ITableLayout> = ({
	headerChildren,
	headerSub,
	tableLayoutRef,
	total,
	handleChange,
	scrollX,
	rightContent,
	inputWrapStyle,
	enableColumnResize = true,
	extHeight = 0,
	...tableProps
}) => {
	const tableWrapRef = useRef<HTMLDivElement>(null);

	const [tableHeight, setTableHeight] = useState<number>(300);
	const [tableWidth, setTableWidth] = useState<number | string>('max-content');
	const [pageIndex, setPageIndex] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

	// 给 Table 兜底一个稳定的 rowKey，避免数据项里没有 key 时出现 React 警告
	const mergedTableProps: TableProps = { ...tableProps };
	if (!mergedTableProps.rowKey) {
		mergedTableProps.rowKey = (record: any) =>
			(record && (record.id ?? record.key ?? record.number ?? record.docId)) ||
			JSON.stringify(record);
	}


	useImperativeHandle(tableLayoutRef, () => ({
		tableHeight,
		offset: (pageIndex - 1) * pageSize,
		pageSize,
		resetTablePage: () => {
			setPageIndex(1);
		},
		changeTablePageSize: number => {
			setPageSize(number);
		},
	}), [pageSize, pageIndex, tableHeight]);

	useLayoutEffect(() => {
		const element = tableWrapRef.current;
		if (!element) return;

		const handleResize = debounce((entries: ResizeObserverEntry[]) => {
			for (const entry of entries) {
				const height = (entry.contentRect.height ?? 300) - 55 - 50 - 20 + (extHeight);
				// Ensure height is never negative (minimum 100px)
				setTableHeight(height);
				setTableWidth(entry.contentRect.width ?? 'max-content');
			}
		}, 300);

		const observer = new ResizeObserver(handleResize);

		observer.observe(element);


		return () => {
			handleResize.cancel();
			observer.disconnect();
		};
	}, [rightContent, tableWrapRef.current, extHeight]);
	const pagination = {
		total: total,
		current: pageIndex,
		onChange: (page: number, ps: number) => {
			flushSync(() => {
				setPageSize(ps);
				setPageIndex(page);
			});
			handleChange && handleChange((page - 1) * ps);
		},
		showSizeChanger: true,
		pageSizeOptions: DEFAULT_PAGE_SIZES,
		pageSize,
		hideOnSinglePage: false,
		showQuickJumper: true,
		onShowSizeChange: () => {
			setPageIndex(1);
		},
	};

	const scroll = {
		// 列宽拖拽开启时，横向宽度交给列本身累加决定，
		// 不再跟随容器宽度自动拉伸，否则列边界会和鼠标位置对不齐。
		x: scrollX ?? (enableColumnResize ? 'max-content' : tableWidth),
		y: tableHeight,
		scrollToFirstRowOnChange: true,
	};

	return (
		<TableLayoutWrap>
			{headerChildren && <div className="input--wrap" style={inputWrapStyle}>{headerChildren}</div>}
			{headerSub && <div className="input-sub">{headerSub}</div>}
			<div className="table--wrap" ref={tableWrapRef}>

				<div className='spliter-warp'>
					<Splitter>
						<Splitter.Panel defaultSize="60%" min="60%" max="80%" className="ant-splitter-panel--wrap">
							{enableColumnResize ? (
								// ResizableTable 保留原本的 antd Table 行为，
								// 只是替换表头单元格，让叶子列支持左右拖拽调宽。
								<ResizableTable<AnyObject>
									bordered
									scroll={scroll}
									{...mergedTableProps}
									pagination={mergedTableProps.pagination === false ? false : pagination}
								/>
							) : (
								<Table
									bordered
									scroll={scroll}
									{...mergedTableProps}
									pagination={mergedTableProps.pagination === false ? false : pagination}
								/>
							)}
						</Splitter.Panel>
						{rightContent && <Splitter.Panel defaultSize="40%" min="20%" max="40%">
							{rightContent}
						</Splitter.Panel>}
					</Splitter>
				</div>

			</div>

		</TableLayoutWrap>
	);
};

export default STableLayout;
